// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import ClosePositionsMutuallyTemplate from '@front/templates/ClosePositionsMutuallyTemplate.html';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { positionsMutualCloseScreenHandler } from '@shared/utils/AppHandlers';
import { DataCache } from '@shared/commons/DataCache';
import { ClosePositionsMutuallyItem } from '../cache/ClosePositionsMutuallyItem';
import { PositionItem } from '../cache/PositionItem';
import { KeyCode, KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { PositionFormatter } from '@shared/commons/cache/Formatters/PositionFormatter';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { WDSettings } from '../settings/WDGeneralSettings';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
export class ClosePositionsMutually extends ApplicationPanelWithTable {
    constructor() {
        super(...arguments);
        this.positionsIdArray = [];
        this.account = null;
        this.productType = null;
        this.instrument = null;
        this.totalValue = null;
        this.placedFrom = null;
        this.displayAmountInLots = null;
        this.closingFunctionReference = null;
        this.skipOnSave = true;
        this.NeedCalculateRowCount = false;
        this.headerLocaleKey = 'panel.ClosePositionsMutually';
        this.Name = PanelNames.ClosePositionsMutually;
    }
    getType() {
        return PanelNames.ClosePositionsMutually;
    }
    oninit() {
        super.oninit();
        this.on('modifyBtnClick', this.mutualPositionsClosing);
        this.on('cancelBtnClick', this.close);
        KeyEventProcessor.OnKeyDown.Subscribe(this.onGlobalKeyDown, this);
        WDSettings.settingsChanged.Subscribe(this.settingsChanged, this);
        ResourcesManager.onLocaleChange.Subscribe(this.localize, this);
    }
    oncomplete() {
        super.oncomplete();
        this.displayAmountInLots = WDSettingsUtils.displayAmountInLots();
        this.localize();
        this.initByPositions();
        this.center();
    }
    localize() {
        super.localize();
        const totalValueSign = !isNullOrUndefined(this.totalValue) ? Math.sign(this.totalValue.value) : 0;
        const totalValuePrefix = totalValueSign === 0
            ? ''
            : totalValueSign > 0
                ? Resources.getResource('general.trading.position.Buy')
                : Resources.getResource('general.trading.position.Sell');
        void this.set({
            symbolLabel: Resources.getResource('panel.ClosePositionsMutually.Symbol'),
            totalValuePrefix,
            totalLabel: Resources.getResource('panel.ClosePositionsMutually.Total'),
            closePosBtnText: Resources.getResource('panel.ClosePositionsMutually.CloseBtn'),
            cancelBtnText: Resources.getResource('panel.ClosePositionsMutually.CancelBtn')
        });
    }
    mutualPositionsClosing() {
        var _a;
        if (((_a = this.positionsIdArray) === null || _a === void 0 ? void 0 : _a.length) === 0) {
            return;
        }
        const posDict = DataCache.Trading.getPositionsById(this.positionsIdArray);
        const p0ID = this.positionsIdArray[0];
        const p1ID = this.positionsIdArray[1];
        const p0 = posDict[0];
        const p1 = posDict[1];
        const pos0DateTime = PositionFormatter.DateTime(p0);
        const pos1DateTime = PositionFormatter.DateTime(p1);
        let mutualPositionsIds = [];
        if (pos0DateTime.getTime() < pos1DateTime.getTime()) {
            mutualPositionsIds = [p0ID, p1ID];
        }
        else {
            mutualPositionsIds = [p1ID, p0ID];
        }
        if (!isNullOrUndefined(this.closingFunctionReference)) {
            this.closingFunctionReference(posDict, this.placedFrom, mutualPositionsIds);
        }
        this.close();
    }
    initByPositions() {
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt)) {
            return;
        }
        qt.ClearRows();
        let totalValue = 0;
        let symbol = null;
        let pT = null;
        let acc = null;
        for (let i = 0; i < this.positionsIdArray.length; i++) {
            const pos = DataCache.Trading.getPositionById(this.positionsIdArray[i]);
            symbol = pos.Instrument;
            acc = pos.Account;
            pT = pos.ProductType;
            totalValue += PositionItem.GetPositionAmount(pos) * PositionItem.GetPositionSign(pos);
            qt.AddItem(new ClosePositionsMutuallyItem(pos));
        }
        // ! Note that the account, product type, and instrument have been taken from the last position of the positionsIdArray !
        this.account = acc;
        this.productType = pT;
        this.instrument = symbol;
        this.totalValue = new Quantity(totalValue, this.displayAmountInLots);
        const totalValuePrefix = totalValue === 0
            ? ''
            : totalValue > 0
                ? Resources.getResource('general.trading.position.Buy')
                : Resources.getResource('general.trading.position.Sell');
        void this.set({
            symbolName: isValidString(symbol) ? symbol.DisplayName() : '',
            totalValueStyle: totalValue === 0 ? '' : totalValue > 0 ? 'buy-color' : 'sell-color',
            totalValuePrefix,
            totalValueFormatted: DataCache.formatVolume(symbol, Math.abs(totalValue), this.displayAmountInLots, pT, acc)
        });
    }
    onGlobalKeyDown() {
        if (KeyEventProcessor.currentButton === KeyCode.ESC) {
            // This window can be closed using ESC button as well.
            void this.set('visible', false);
        } // this.close();
    }
    dispose() {
        WDSettings.settingsChanged.UnSubscribe(this.settingsChanged, this);
        ResourcesManager.onLocaleChange.UnSubscribe(this.localize, this);
        KeyEventProcessor.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
        super.dispose();
    }
    layoutTable() {
        const qtR = this.quickTableRactive;
        if (isNullOrUndefined(qtR))
            return;
        qtR.setSizes();
    }
    settingsChanged() {
        const newSettingsInLots = WDSettingsUtils.displayAmountInLots();
        if (this.displayAmountInLots === newSettingsInLots) {
            return;
        }
        this.displayAmountInLots = newSettingsInLots;
        const ins = this.instrument;
        if (!isNullOrUndefined(ins)) {
            void this.set('totalValueFormatted', DataCache.formatVolume(ins, Quantity.convertQuantityValue(this.totalValue, ins, newSettingsInLots, this.account, this.productType), newSettingsInLots));
        }
    }
    static show(positionsIdArray, placedFrom, closeFunction) {
        const panel = MainWindowManager.Factory.addPanel(PanelNames.ClosePositionsMutually);
        panel.placedFrom = placedFrom;
        panel.positionsIdArray = positionsIdArray;
        panel.closingFunctionReference = closeFunction;
        return panel;
    }
    static positionsMutualCloseScreenHandlerInitialize() {
        positionsMutualCloseScreenHandler.show = ClosePositionsMutually.show;
    }
}
ApplicationPanelWithTable.extendWith(ClosePositionsMutually, {
    partials: { bodyPartial: ClosePositionsMutuallyTemplate },
    data: function () {
        return {
            dockablePanel: false,
            attached: false,
            isAccountLinkShow: false,
            isSymbolLinkShow: false,
            showFooter: false,
            showHeader: true,
            style_addition_header: 'js-MutualClose-AdditionalHeader',
            totalValueStyle: '',
            symbolLabel: 'Symbol:', // example values
            symbolName: 'AUD/CAD', // <
            totalLabel: 'Total: 0', // <
            totalValueFormatted: '0.00', // <
            closePosBtnText: 'Close', // <
            cancelBtnText: 'Cancel' // example values
        };
    }
});
